import React from 'react'
import '../style/journey-configuration-form.scss'
import { JourneyBuilderMode } from '../enums'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { SegmentDto } from '../../../dtos/segment'
import { DomainDto } from '../../../dtos/domain'
import { DomainService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { ConfigurationContext } from './configuration-context'
import { GenericDetailsWell } from './generic-details-well'
import { SendSettingsWell } from './send-settings-well'
import { AudienceWell } from './audience-well'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import { IConfigurationFlags } from './interfaces'
import { SegmentSource } from '@pushly/models/lib/enums/segment-source'
import { HIDDEN_IN_CREATION_FLOWS } from '../../../constants'
import { getAvailableSegmentTypes } from '../../../_utils/domain'
import { JourneyType, TriggerType } from '@pushly/models/lib/enums/journeys'
import JourneyEntryWell from '../../../features/journeys/components/entry/journey-entry-well'
import type { Journey } from '../../../features/journeys/types/journey-api-response'

interface IConfigurationEditor {
    mode: JourneyBuilderMode
    loading?: boolean
    journey: Journey
    editableState?: JourneyEditableState
    domain?: DomainDto
    onChange?: (value: Journey) => void
    // primary action button
    onSubmit?: (value: Journey, flags: IConfigurationFlags) => void
    // save only
    onSave?: (value: Journey, flags: IConfigurationFlags) => void
    disableSubmit?: boolean
    disableRouterPrompt?: boolean
}

interface IState {
    defaultSegment?: SegmentDto
    flags: IConfigurationFlags
}

let CHANGE_DEBOUNCE_TIMEOUT: NodeJS.Timeout

export class ConfigurationEditor extends React.Component<IConfigurationEditor, IState> {
    protected domainService: DomainService

    private unmounting: boolean = false

    public constructor(props: IConfigurationEditor) {
        super(props)

        this.domainService = Container.get(DomainService)

        const config = this.props.journey.configuration
        const hasQuietHours = !!config.quiet_hours?.start
        const hasItemGroups = !!config.item_group_targeting?.item_group_ids

        this.state = {
            flags: {
                quietHoursEnabled: hasQuietHours,
                itemGroupsEnabled: hasItemGroups,
            },
        }
    }

    public componentDidMount() {
        // initial flags set
        this.setFlagsState()

        // initial call to load default segment
        this.fetchSegments()
    }

    public componentDidUpdate(prevProps: IConfigurationEditor) {
        // ensure flags are reset when parent passes changes
        this.setFlagsState(prevProps)
    }

    public componentWillUnmount() {
        this.unmounting = true
    }

    public render() {
        const {
            mode,
            loading,
            domain,
            journey,
            onSubmit,
            onSave,
            disableSubmit,
            editableState,
        } = this.props

        const journeyType = journey.type

        const trigger = extractJourneyTrigger(journey)
        const isSubscribedTrigger = trigger?.configuration?.type === TriggerType.SUBSCRIBED
        const hasTriggerSelected =
            journeyType !== JourneyType.TRIGGER || !!trigger?.configuration?.type

        const isCreateMode = mode === JourneyBuilderMode.CREATE
        const submitText = 'Save & Continue'
        const submitActions = isCreateMode
            ? undefined
            : [
                  {
                      text: 'Save',
                      onClick: () => {
                          return onSave?.(this.props.journey, this.state.flags)
                      },
                  },
              ]

        const submitConfig = {
            onSubmit: () => {
                return onSubmit?.(this.props.journey, this.state.flags)
            },
            submitText,
            submitActions,
            disableSubmit,
            hideCancel: true,
        }

        return (
            <ConfigurationContext.Provider
                value={{
                    mode,
                    loading: loading ?? false,
                    domain: domain!,
                    domainDefaultSegment: this.state.defaultSegment,
                    journey,
                    setJourney: this.emitChange,
                    editableState: editableState ?? JourneyEditableState.EDITABLE,
                    flags: this.state.flags,
                    setFlags: (_flags: Partial<IConfigurationFlags>) =>
                        this.setState(({ flags }) => ({
                            flags: {
                                ...flags,
                                ..._flags,
                            },
                        })),
                }}
            >
                <GenericDetailsWell />
                {!!journeyType && <JourneyEntryWell />}
                {!!journeyType && hasTriggerSelected && (
                    <AudienceWell
                        submitConfiguration={isSubscribedTrigger ? submitConfig : undefined}
                        getSegments={this.fetchSegments}
                    />
                )}
                {!!journeyType && hasTriggerSelected && !isSubscribedTrigger && (
                    <SendSettingsWell submitConfiguration={submitConfig} />
                )}
            </ConfigurationContext.Provider>
        )
    }

    protected setFlagsState(prevProps?: IConfigurationEditor) {
        const flagUpdates: Partial<IConfigurationFlags> = {}

        const prevJourney = prevProps?.journey
        const prevTriggerParams = prevJourney ? extractJourneyTrigger(prevJourney)?.configuration?.params : undefined

        const currJourney = this.props.journey
        const currTriggerParams = extractJourneyTrigger(currJourney)?.configuration?.params

        if (!prevTriggerParams?.criteria && currTriggerParams?.criteria) {
            flagUpdates.criteriaEnabled = true
        }

        if (!prevJourney?.configuration.quiet_hours?.start && !!currJourney.configuration.quiet_hours?.start) {
            flagUpdates.quietHoursEnabled = true
        }

        if (
            !prevJourney?.configuration.item_group_targeting?.item_group_ids &&
            !!currJourney.configuration.item_group_targeting?.item_group_ids
        ) {
            flagUpdates.itemGroupsEnabled = true
        }

        if (!this.unmounting && Object.keys(flagUpdates).length > 0) {
            this.setState(({ flags }) => ({
                flags: {
                    ...flags,
                    ...flagUpdates,
                },
            }))
        }
    }

    protected emitChange = async (changes: Journey, debounce: boolean = false) => {
        clearTimeout(CHANGE_DEBOUNCE_TIMEOUT)
        CHANGE_DEBOUNCE_TIMEOUT = setTimeout(
            () => {
                this.props.onChange?.(changes)
            },
            debounce ? 320 : 0,
        )
    }

    protected fetchSegments = async (): Promise<SegmentDto[]> => {
        const { data } = await this.domainService.fetchSegmentsByDomainId(this.props.domain!.id, {
            query: {
                includeDefault: 1,
                includeTreated: 0,
                pagination: 0,
                fields: 'id,name,source,segment_type,flags,computed_status,is_default,icon_url,last_targeted_date_utc',
                source: SegmentSource.STANDARD,
                order: JSON.stringify({
                    field: 'segment.last_targeted_date_utc',
                    direction: 'descend',
                    nulls: 'LAST',
                }),
            },
            showLoadingScreen: false,
        })

        const allowedSegmentTypes = getAvailableSegmentTypes(this.props.domain!)

        const availableSegments = data.filter(
            (s) => allowedSegmentTypes.includes(s.type) && !s.flags.includes(HIDDEN_IN_CREATION_FLOWS),
        )

        if (!this.unmounting && !this.state.defaultSegment) {
            const defaultSegment = data.find(({ isDefault }) => isDefault)
            this.setState({ defaultSegment })

            if (defaultSegment && this.props.journey?.type === JourneyType.TRIGGER) {
                const triggerType = extractJourneyTrigger(this.props.journey)?.configuration?.type
                const segmentIds = this.props.journey.configuration?.audience?.segment_ids
                const hasSegments = Array.isArray(segmentIds) && segmentIds.length > 0

                if (!hasSegments) {
                    const updated = structuredClone(this.props.journey)
                    updated.configuration.audience = updated.configuration.audience ?? {}
                    updated.configuration.audience.segment_ids = [defaultSegment.id]

                    this.emitChange(updated)
                }
            }
        }

        return availableSegments
    }
}
